import React from 'react';
import { Languages } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import type { LocaleSignalSummary } from '../../types/traffic';
const BASIS_LABELS: Record<string, string> = {
  'secondary language': 'Secondary accepted language',
  calendar: 'Calendar system',
  'digit system': 'Digit system'
};
interface LocaleSignalsCardProps {
  signals: LocaleSignalSummary;
  uniqueVisitors: number;
}

/**
 * Regional signals buried in OS preferences rather than browser menus: the
 * calendar, the digit system, and the full accepted-language list. These survive a
 * changed time zone, which is what makes them useful where the zone reads UTC.
 */
export function LocaleSignalsCard({
  signals,
  uniqueVisitors
}: LocaleSignalsCardProps) {
  const {
    withSignals,
    tension,
    byOrigin,
    byBasis
  } = signals;
  const tensionRate = withSignals > 0 ? Math.round(tension / withSignals * 100) : 0;
  const coverage = uniqueVisitors > 0 ? Math.min(100, Math.round(withSignals / uniqueVisitors * 100)) : 0;
  const hasData = withSignals > 0;
  return <Card data-id="element-120" data-mp="1qbdzufh1wcpx">
      <CardHeader data-id="element-121" data-mp="1qbx977smxssc">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-122" data-mp="1qcgrwqq40l7f">
          <CardTitle className="flex items-center gap-2" data-id="element-123" data-mp="1li2t0ec8nvy0">
            <Languages className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-124" data-mp="2080mn9sxtdul" />
            Locale internals
          </CardTitle>
          <Badge variant="outline" data-id="element-125" data-mp="1v8novu0j2md8">Survives spoofing</Badge>
        </div>
        <CardDescription data-id="element-126" data-mp="299i00keyn1fl">
          The calendar, digit system, and full accepted-language list sit in OS regional
          preferences, so they persist when the time zone is changed
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-127" data-mp="evpflvv6lrst">
        {hasData ? <>
            <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-128" data-mp="au3uxoceo982">
              <div data-id="element-129" data-mp="avorfjia628k">
                <p className="font-mono text-2xl tabular-nums text-foreground" data-id="element-130" data-mp="29ydgwhd5a7lu">{tensionRate}%</p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-131" data-mp="2exgmq40y8huv">
                  locale tension — internals disagree with the primary language
                </p>
              </div>
              <div data-id="element-132" data-mp="2jyw6lsre4ibo">
                <p className="font-mono text-2xl tabular-nums text-foreground" data-id="element-133" data-mp="2ovcvip6ez0pt">
                  {tension.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-134" data-mp="2swg6ax9lu6k6">
                  of {withSignals.toLocaleString()} visitors reporting internals ({coverage}%
                  coverage)
                </p>
              </div>
            </div>

            {byBasis.length > 0 && <div className="mt-6" data-id="element-135" data-mp="37l2or7uj3efm">
                <p className="text-xs font-medium text-foreground" data-id="element-136" data-mp="37o6y0jmup0d5">
                  Visitors whose region came only from a locale internal
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-137" data-mp="3gnu1rc31yi9d">
                  These would have resolved to nothing on time zone alone.
                </p>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" data-id="element-138" data-mp="10xvd84628r7l">
                  {byBasis.map(row => <span key={row.basis} className="text-xs" data-id="element-139" data-mp="vy8xno2reyvm">
                      <span className="font-mono tabular-nums text-foreground" data-id="element-140" data-mp="w0lo6pml2hka">{row.visitors}</span>{' '}
                      <span className="text-muted-foreground" data-id="element-141" data-mp="r20nkp3f5vvb">
                        {BASIS_LABELS[row.basis] ?? row.basis}
                      </span>
                    </span>)}
                </div>
              </div>}

            {byOrigin.length > 0 && <div className="-mx-1 mt-6 overflow-x-auto px-1" data-id="element-142" data-mp="1eva2n8wbcyzk">
                <p className="mb-3 text-xs font-medium text-foreground" data-id="element-143" data-mp="19xqn85nvqg61">
                  Where visitors with locale tension resolve to
                </p>
                <Table data-id="element-144" data-mp="22nh60s0gi68h">
                  <TableHeader data-id="element-145" data-mp="22m62mtaxb0hw">
                    <TableRow data-id="element-146" data-mp="22lwcn5pvotc7">
                      <TableHead data-id="element-147" data-mp="1n57khblqrf41">Region</TableHead>
                      <TableHead data-id="element-148" data-mp="1n3mmfc07xe1f">Resolved by</TableHead>
                      <TableHead className="text-right" data-id="element-149" data-mp="1r6j6z89bjk80">Visitors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-150" data-mp="1d8bfvgyyrd4r">
                    {byOrigin.map(row => <TableRow key={`${row.origin}-${row.basis}`} data-id="element-151" data-mp="1i5bdd3hqz7k9">
                        <TableCell className="whitespace-nowrap text-xs text-foreground" data-id="element-152" data-mp="25vp6kxc2jcgf">
                          {row.origin}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground" data-id="element-153" data-mp="2au8qytb0kg6o">
                          {BASIS_LABELS[row.basis] ?? row.basis}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-154" data-mp="213xgsp0qlarc">
                          {row.visitors.toLocaleString()}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>}
          </> : <p className="text-xs leading-relaxed text-muted-foreground" data-id="element-155" data-mp="i3y87wm85ki6">
            No locale internals recorded yet. These are captured on views written after this was
            added — older rows have no values and cannot be backfilled, since the settings only
            exist in the visitor&rsquo;s browser at the moment of the request.
          </p>}

        <div className="mt-6 space-y-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-156" data-mp="3vdckrxyl9fvs">
          <p data-id="element-157" data-mp="3qfszie3h42uc">
            <span className="font-medium text-foreground" data-id="element-158" data-mp="883i6gfo8n5d">Why this catches more:</span> Tor Browser
            forces the time zone to UTC and the language to en-US, but does not normalise the
            calendar or digit system — so a Persian calendar or extended-Arabic digits still points
            at a region. And setting a browser to English usually leaves secondary entries like{' '}
            <span className="font-mono" data-id="element-159" data-mp="1eqjjgx8kj006">fa</span> or <span className="font-mono" data-id="element-160" data-mp="cbkd2mm4neqx">ar-sy</span> in the
            accepted-language list.
          </p>
          <p data-id="element-161" data-mp="h8sz4yaec2xg">
            <span className="font-medium text-foreground" data-id="element-162" data-mp="h8asd7q3y6ba">Why it is only a signal:</span> people
            legitimately prefer another calendar or keyboard, expatriates keep their home settings,
            and anyone deliberately evading detection can change these too. Directional at aggregate
            scale; never evidence about a person.
          </p>
        </div>
      </CardContent>
    </Card>;
}